/**
 * RoleTitlePicker — a Role Master title chosen from a lookup, never typed
 * (Victor, 7 Sep 2026: the next possible position comes from the growth plan
 * when it is set there, else from Role Master). The value is the title as
 * Role Master spells it.
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown, X } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import roleMasterService, { type RoleTitle } from '@/services/api/roleMasterService'

export function useRoleTitles(enabled = true) {
  return useQuery<RoleTitle[]>({
    queryKey: ['role-master', 'titles'],
    queryFn: () => roleMasterService.listTitles(),
    enabled,
    staleTime: 10 * 60 * 1000,
  })
}

interface RoleTitlePickerProps {
  value: string
  onChange: (title: string) => void
  disabled?: boolean
  placeholder?: string
  ariaLabel?: string
  className?: string
}

export default function RoleTitlePicker({
  value,
  onChange,
  disabled,
  placeholder = 'Choose a role from Role Master',
  ariaLabel,
  className,
}: RoleTitlePickerProps) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const { data: titles = [], isLoading } = useRoleTitles(open)
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const list = s
      ? titles.filter((t) => t.title.toLowerCase().includes(s) || (t.job_family || '').toLowerCase().includes(s))
      : titles
    return list.slice(0, 80)
  }, [titles, q])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn('h-8 w-full justify-between text-sm font-normal', !value && 'text-muted-foreground', className)}
        >
          <span className="truncate">{value || placeholder}</span>
          <ChevronsUpDown className="ml-2 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-2" align="start">
        <Input
          autoFocus
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search Role Master titles"
          className="h-8 text-sm"
          aria-label="Search role titles"
        />
        <ul role="listbox" className="mt-2 max-h-64 overflow-y-auto text-sm">
          {isLoading && <li className="px-2 py-1.5 text-muted-foreground">Loading titles…</li>}
          {!isLoading && filtered.length === 0 && <li className="px-2 py-1.5 text-muted-foreground">No title matches.</li>}
          {filtered.map((t) => {
            const selected = t.title === value
            return (
              <li key={t.role_code}>
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(t.title)
                    setOpen(false)
                    setQ('')
                  }}
                  className={cn('flex w-full items-center gap-2 rounded px-2 py-1.5 text-left hover:bg-muted', selected && 'bg-muted')}
                >
                  <Check className={cn('h-3.5 w-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
                  <span className="min-w-0 flex-1 truncate">{t.title}</span>
                  {t.job_family && <span className="shrink-0 text-[11px] text-muted-foreground">{t.job_family}</span>}
                </button>
              </li>
            )
          })}
        </ul>
        {value && !disabled && (
          <div className="mt-2 border-t pt-2">
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7 gap-1 px-2 text-xs"
              onClick={() => {
                onChange('')
                setOpen(false)
              }}
            >
              <X className="h-3 w-3" /> Clear
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
